import styled from '@emotion/styled';
import { Button, Card, Checkbox, FormControlLabel, Grid, Typography } from '@material-ui/core';
import _QRCode from 'qrcode.react';
import { createElement, FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { CreationForShowFragment } from '../../../../generated/graphql';
import { SingleAssignmentFragment } from '../../../../generated/graphql_offer';
import theme from '../../../../theme';
import { SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import mq from '../../../styleUtility/mediaquery';
import AvatarAndName from '../../compound/AvatarAndName';
import AspectRatioFixed from '../../primitive/AspectRatioFixed';
import ImageScreen from '../../primitive/ImageScreen';
import PublicId from '../../primitive/PublicId';
import PreviewVideoScreen from '../PreviewVideoScreen';
import IntentDialog from './IntentDialog';

export type Props = {
  creation: CreationForShowFragment;
  assignment: SingleAssignmentFragment | undefined;
  requestLatestDeliverableCreativeUrl: () => string | null | undefined;
  profileUrl: string;
  handleOpen?: (creationId: string) => void;
  className?: string;
  addOrRemoveCheckedProposal: (checked: string) => void;
  checkedList: string[];
};

const ProposalItem: FC<Props> = ({
  creation,
  assignment,
  requestLatestDeliverableCreativeUrl,
  profileUrl,
  className,
  addOrRemoveCheckedProposal,
  checkedList,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [checked, setChecked] = useState(false);

  const handleOpen = useCallback(() => {
    setOpenModal(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpenModal(false);
  }, []);

  useEffect(() => {
    setChecked(checkedList.includes(creation.id));
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedList]);

  const { network, placement, attribute } = creation.creativeFormat;
  const networkAndPlacementMessage = `${network.name} ${placement.name}`;

  const playbackTimeMessage = useMemo(() => {
    return attribute.playbackTime ? (
      <FormattedMessage id="creative_format.playback_time" values={{ seconds: attribute.playbackTime.seconds }} />
    ) : (
      ''
    );
  }, [attribute.playbackTime]);

  const aspectRatioMessage = useMemo(() => {
    return attribute.aspectRatio
      ? attribute.aspectRatio.aspectRatio.width === 0 &&
        attribute.aspectRatio.aspectRatio.height === 0 &&
        attribute.aspectRatio.size.width === 0 &&
        attribute.aspectRatio.size.height === 0
        ? ''
        : `${attribute.aspectRatio.aspectRatio.width}:${attribute.aspectRatio.aspectRatio.height}`
      : '';
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDesabledDownloadOrUpload = !(assignment?.status == 'COMPLETED');
  const latestDeliverableCreativeUrl = requestLatestDeliverableCreativeUrl();

  return (
    <Root container direction="column" alignItems={'center'}>
      <Grid item>
        <AdoptCheck
          checked={checked}
          control={<Checkbox color="primary" onChange={() => addOrRemoveCheckedProposal(creation.id)} />}
          label={<FormattedMessage id="proposal_list.item.check.label" />}
        />
      </Grid>
      <ItemGrid item>
        <Card
          className={className}
          style={{
            border: checked ? `1.5px solid ${theme.palette.primary.main}` : 'none',
          }}
        >
          <CardHeader>
            <Name variant="subtitle1">
              <CreativeFormat color="textSecondary" noWrap>
                {networkAndPlacementMessage} {aspectRatioMessage} {playbackTimeMessage}
              </CreativeFormat>
              <CreationId id={creation.id} color="textSecondary" noWrap />
            </Name>
          </CardHeader>
          <div>
            {creation.creativeFormat.creativeType.key === 'video' && (
              <Thumbnail aspectRatio={[4, 3]}>
                <PreviewVideoScreen
                  src={latestDeliverableCreativeUrl}
                  controls
                  nodownload={isDesabledDownloadOrUpload}
                />
              </Thumbnail>
            )}
            {creation.creativeFormat.creativeType.key === 'static_image' && (
              <Thumbnail aspectRatio={[4, 3]}>
                <ImageScreen src={latestDeliverableCreativeUrl} />
              </Thumbnail>
            )}
          </div>
          <CardFooter container>
            <Grid container item xs={8} justifyContent={'flex-start'}>
              <Grid item>
                <ProfileLink variant="text" href={`${profileUrl}/users/${creation.assignee!.id}`} target="_blank">
                  <AvatarAndName user={creation.assignee!} />
                </ProfileLink>
              </Grid>
              <Grid item>
                <IntentButton variant="outlined" color="primary" onClick={handleOpen}>
                  <FormattedMessage id="show_project_page.proposal_list.intent_button" />
                </IntentButton>
              </Grid>
            </Grid>
            <QRCodeGrid item xs={4}>
              {latestDeliverableCreativeUrl ? (
                <QRCode
                  value={
                    latestDeliverableCreativeUrl.slice(0, 2) === '//'
                      ? `${window.location.protocol}${latestDeliverableCreativeUrl}`
                      : latestDeliverableCreativeUrl
                  }
                  size={88}
                  level="L"
                  renderAs="svg"
                />
              ) : null}
            </QRCodeGrid>
          </CardFooter>
          <IntentDialog open={openModal} onRequestClose={handleClose} creation={creation} key={creation.id} />
        </Card>
      </ItemGrid>
    </Root>
  );
};

const Root = styled(Grid)({});

const CardHeader = styled('div')({
  paddingTop: SPACING_REGULAR,
  paddingRight: SPACING_UNIT * 3,
  paddingBottom: SPACING_REGULAR,
  paddingLeft: SPACING_UNIT * 3,
  [mq('sp')]: {
    paddingRight: SPACING_UNIT * 2,
    paddingLeft: SPACING_UNIT * 2,
  },
});

const Thumbnail = styled(AspectRatioFixed)({
  zIndex: 1,
});

const CardFooter = styled(Grid)({
  padding: SPACING_UNIT,
});

const QRCodeGrid = styled(Grid)({
  textAlign: 'end',
});

const QRCode = styled(_QRCode)({
  [mq('sp')]: {
    display: 'none',
  },
});

const ProfileLink = (styled(Button)({
  padding: SPACING_UNIT,
  textTransform: 'none',
}) as unknown) as typeof Button;

const AdoptCheck = styled(FormControlLabel)({
  marginLeft: SPACING_REGULAR,
});

const Name = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: 'calc(100% - 32px)',
  [mq('sp')]: {
    fontSize: 13,
  },
});

const IntentButton = styled(Button)({
  margin: SPACING_UNIT,
});

const CreativeFormat = styled(Typography)({
  fontSize: 18,
  [mq('sp')]: {
    fontSize: 12,
    '& div': {
      fontSize: 12,
    },
  },
});

const CreationId = styled(PublicId)({
  [mq('sp')]: {
    fontSize: 12,
    '& div': {
      fontSize: 12,
    },
  },
});

const ItemGrid = styled(Grid)({
  border: `1px solid ${theme.palette.grey['400']}`,
  width: '100%',
});

export default ProposalItem;
